import React, { ReactElement, useEffect } from 'react';
import get from 'get-value';
import classNames from 'classnames';
import { removeArrayBrackets } from 'utils';

import FormControl from 'shared/FormControl/FormControl';
import { QuoteFields } from './QuoteField';
import { Select as CustomSelect } from 'shared/Inputs';

import './_QuoteSelect.scss';
import { Option } from 'shared/interface/globalInterface';
import { HelperContent } from 'shared/HelperText/HelperTextInterface';

interface IQuoteSelect extends QuoteFields {
  options: Option[];
  form: any;
  field: any;
  onClick?: any;
  helperContent?: HelperContent;
  searchable?: boolean;
  bottomHelperText?: string;
  bottomTextVariant?: ColorVariants;
  bottomHelperElement?: ReactElement;
}

enum ColorVariants {
  RED = 'red',
}

export const QuoteSelect: React.FC<IQuoteSelect> = ({
  labelText,
  paragraphText,
  options,
  isValidNested,
  isCorrectNested,
  helperText,
  helperContent,
  field,
  tooltip,
  form,
  required,
  disabled,
  onClick,
  searchable,
  bottomHelperText,
  bottomTextVariant,
  bottomHelperElement,
}) => {
  const { name, value } = field;
  const fieldName = removeArrayBrackets(name);
  const { errors, touched, setFieldValue, setFieldTouched } = form;
  const isValid = get(errors, fieldName) && get(touched, fieldName);
  const isCorrect = !get(errors, fieldName) && get(touched, fieldName);
  const selectedOption = options.find((o) => o.value === field.value);
  const selectedLabel = selectedOption ? selectedOption.option : '';

  useEffect(() => {
    if (value) {
      setFieldTouched(name);
    }
  }, [value, setFieldTouched, name]);

  const handleSelected = (option: Option) => {
    setFieldValue(name, option.value);

    if (onClick) {
      onClick(option);
    }
  };

  const bottomHelperClass = classNames('bottom-helper-text', {
    [`bottom-helper-text--${bottomTextVariant}`]: bottomTextVariant,
  });

  return (
    <FormControl
      tooltip={tooltip}
      labelText={labelText}
      paragraphText={paragraphText}
      error={isValidNested || isValid}
      helperText={helperText}
      helperContent={helperContent}
      isCorrect={isCorrectNested || isCorrect}
      required={required}
      fieldName={fieldName}>
      <div className="quote-select">
        <select hidden={true} className="select" {...field} disabled={disabled} value={value || undefined}>
          <option disabled value="" className="placeholder">
            Please select...
          </option>
          {options.map((option) => (
            <option key={`${fieldName}-${option.id || option.value}`} value={(option.value || '').toString()}>
              {option.option}
            </option>
          ))}
        </select>

        <CustomSelect
          options={options}
          name={field.name}
          onSelected={handleSelected}
          disabled={disabled || false}
          selected={{ value, label: selectedLabel }}
          isRequired={required}
          searchable={searchable}
        />
        {bottomHelperText && <p className={bottomHelperClass}>{bottomHelperText}</p>}
        {bottomHelperElement && <div className="bottom-helper-element-class">{bottomHelperElement}</div>}
      </div>
    </FormControl>
  );
};
